import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { usersAPI } from '../services/api'
import { toast } from 'react-hot-toast'

export const useUserActions = () => {
  const [isLoading, setIsLoading] = useState(false)
  const queryClient = useQueryClient()

  const updateUserStatus = async (userId, { status, reason }) => {
    setIsLoading(true)
    try {
      const response = await usersAPI.updateUserStatus(userId, {
        status,
        reason,
      })

      // Invalidate and refetch users data
      queryClient.invalidateQueries(['admin', 'users'])
      
      toast.success(`Kullanıcı durumu "${status}" olarak güncellendi`)
      return response.data.data
    } catch (error) {
      const message = error.response?.data?.message || 'Kullanıcı durumu güncellenirken hata oluştu'
      toast.error(message)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const updateUserCredits = async (userId, { amount, operation, reason }) => {
    setIsLoading(true)
    try {
      const response = await usersAPI.updateUserCredits(userId, {
        amount,
        operation,
        reason,
      })

      // Invalidate and refetch users data
      queryClient.invalidateQueries(['admin', 'users'])
      queryClient.invalidateQueries(['admin', 'user-details', userId])
      
      const operationText = operation === 'add' ? 'eklendi' : 'çıkarıldı'
      toast.success(`${amount} kredi başarıyla ${operationText}`)
      return response.data.data
    } catch (error) {
      const message = error.response?.data?.message || 'Kredi güncellenirken hata oluştu'
      toast.error(message)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const addUserNote = async (userId, { note, category }) => {
    setIsLoading(true)
    try {
      const response = await usersAPI.addUserNote(userId, {
        note,
        category,
      })

      // Invalidate and refetch user notes
      queryClient.invalidateQueries(['admin', 'user-notes', userId])
      queryClient.invalidateQueries(['admin', 'user-details', userId])
      
      toast.success('Kullanıcı notu başarıyla eklendi')
      return response.data.data
    } catch (error) {
      const message = error.response?.data?.message || 'Not eklenirken hata oluştu'
      toast.error(message)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const deleteUser = async (userId) => {
    setIsLoading(true)
    try {
      const response = await usersAPI.deleteUser(userId)

      // Invalidate and refetch users data
      queryClient.invalidateQueries(['admin', 'users'])
      
      toast.success('Kullanıcı başarıyla silindi')
      return response.data.data
    } catch (error) {
      const message = error.response?.data?.message || 'Kullanıcı silinirken hata oluştu'
      toast.error(message)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const exportUsers = async (filters = {}) => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams()

      // Apply filters
      if (filters.status && filters.status !== 'all') {
        params.append('status', filters.status)
      }
      if (filters.dateRange) {
        if (filters.dateRange.startDate) {
          params.append('dateFrom', filters.dateRange.startDate.toISOString().split('T')[0])
        }
        if (filters.dateRange.endDate) {
          params.append('dateTo', filters.dateRange.endDate.toISOString().split('T')[0])
        }
      }

      const response = await usersAPI.exportUsers(Object.fromEntries(params))

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `users-export-${new Date().toISOString().split('T')[0]}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      toast.success('Kullanıcılar başarıyla dışa aktarıldı')
    } catch (error) {
      const message = error.response?.data?.message || 'Dışa aktarma sırasında hata oluştu'
      toast.error(message)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  return {
    updateUserStatus,
    updateUserCredits,
    addUserNote,
    deleteUser,
    exportUsers,
    isLoading,
  }
}